import type { EditableQuiz } from '@/components/builder/QuizFields';

type Question = EditableQuiz['questions'][number];

export interface QuestionProblem {
  /** A blocking problem cannot be saved; a hint only asks for a look. */
  blocking: boolean;
  text: string;
}

/** Players guess the longer answer, so a right answer this much longer gives itself away. */
const GIVEAWAY_RATIO = 1.6;
const GIVEAWAY_MIN_CHARS = 8;

/** What the editor flags on a question, the one place the rule is written. */
export function questionProblem(question: Question): QuestionProblem | null {
  if (!question.text.trim()) return { blocking: true, text: 'Write the question.' };
  const labels = question.options.map((option) => option.label.trim());
  if (labels.some((label) => !label)) return { blocking: true, text: 'Every answer needs words.' };
  if (new Set(labels.map((label) => label.toLowerCase())).size < labels.length) {
    return { blocking: true, text: 'Two answers say the same thing.' };
  }
  const right = question.options.find((option) => option.isCorrect)?.label.trim() ?? '';
  const longestWrong = Math.max(
    ...question.options.filter((option) => !option.isCorrect).map((o) => o.label.trim().length)
  );
  if (
    right.length >= longestWrong * GIVEAWAY_RATIO &&
    right.length - longestWrong >= GIVEAWAY_MIN_CHARS
  ) {
    return {
      blocking: false,
      text: 'The right answer is much longer than the other. Players often pick the longer one.',
    };
  }
  return null;
}
